import React from "react";
import { Link } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { DotShaderBackground } from "../DotShaderBackground";

const LOGO_IMG_SRC = `${process.env.PUBLIC_URL || ""}/favicon.png`;

const MARKETING_SHELL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

  .marketing-app *, .marketing-app *::before, .marketing-app *::after { box-sizing: border-box; }

  .marketing-app {
    --bg: #0a0a0c;
    --border: #2a2a36;
    --accent: #f97316;
    --accent2: #fb923c;
    --accent-dim: rgba(249, 115, 22, 0.15);
    --text: #f9fafb;
    --muted: #9ca3af;
    --font: 'Syne', system-ui, sans-serif;
    --mono: 'JetBrains Mono', monospace;
    min-height: 100vh;
    color: var(--text);
    font-family: var(--font);
    -webkit-font-smoothing: antialiased;
  }

  .marketing-app .m-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    max-width: 1120px;
    margin: 0 auto;
    padding: 28px 32px 32px;
    border-bottom: 1px solid var(--border);
  }

  .marketing-app .m-logo-link {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    text-decoration: none;
    color: inherit;
  }

  .marketing-app .m-logo-mark {
    box-sizing: border-box;
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
    background: var(--bg);
    box-shadow: 0 0 0 2px var(--accent), 0 0 14px var(--accent-dim), 0 4px 12px rgba(0,0,0,0.4);
  }

  .marketing-app .m-logo-mark img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .marketing-app .m-logo-text {
    font-size: 24px;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: var(--text);
  }

  .marketing-app .m-nav-right {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .marketing-app .m-nav-links {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .marketing-app .m-nav-links a {
    color: var(--muted);
    text-decoration: none;
    font-size: 13px;
    font-weight: 500;
  }

  .marketing-app .m-nav-links a:hover {
    color: var(--accent2);
  }

  .marketing-app .m-cta {
    padding: 8px 14px;
    border-radius: 999px;
    border: 1px solid rgba(249,115,22,0.55);
    background: rgba(249,115,22,0.1);
    color: var(--accent2);
    font-size: 12px;
    font-family: var(--mono);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    text-decoration: none;
    font-weight: 600;
  }

  .marketing-app .m-cta:hover {
    background: rgba(249,115,22,0.18);
  }

  .marketing-app .m-main {
    max-width: 720px;
    margin: 0 auto;
    padding: 40px 32px 64px;
  }

  .marketing-app .m-prose h1 {
    font-size: clamp(1.75rem, 4vw, 2.25rem);
    font-weight: 800;
    letter-spacing: -0.03em;
    margin-bottom: 12px;
    line-height: 1.15;
  }

  .marketing-app .m-prose .m-lede {
    font-size: 1.05rem;
    color: var(--muted);
    line-height: 1.6;
    margin-bottom: 32px;
  }

  .marketing-app .m-prose h2 {
    font-size: 1.15rem;
    font-weight: 700;
    margin: 28px 0 12px;
    color: var(--text);
  }

  .marketing-app .m-prose p, .marketing-app .m-prose li {
    font-size: 15px;
    line-height: 1.65;
    color: #d4d4d8;
    margin-bottom: 12px;
  }

  .marketing-app .m-prose ul, .marketing-app .m-prose ol {
    padding-left: 1.25rem;
    margin-bottom: 16px;
  }

  .marketing-app .m-prose li { margin-bottom: 8px; }

  .marketing-app .m-prose a {
    color: var(--accent2);
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .marketing-app .m-prose a:hover { color: var(--accent); }

  .marketing-app .m-footer {
    border-top: 1px solid var(--border);
    margin-top: 24px;
    padding: 24px 32px 40px;
  }

  .marketing-app .m-footer-inner {
    max-width: 1120px;
    margin: 0 auto;
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    color: var(--muted);
  }

  .marketing-app .m-footer-links {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 18px;
    align-items: center;
    justify-content: flex-end;
    flex: 1 1 auto;
  }

  .marketing-app .m-footer-links a {
    color: var(--muted);
    text-decoration: none;
    font-weight: 500;
  }

  .marketing-app .m-footer-links a:hover {
    color: var(--accent2);
  }

  .marketing-app .m-footer-brand {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    color: #d4d4d8;
  }

  .marketing-app .m-footer-mark {
    width: 20px;
    height: 20px;
    padding: 4px;
    border-radius: 50%;
    overflow: hidden;
    background: var(--bg);
    box-shadow: 0 0 0 1px var(--accent), 0 0 8px var(--accent-dim);
  }

  .marketing-app .m-footer-mark img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transform: scale(1.1);
    display: block;
  }
`;

export const MarketingShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="marketing-app">
    <style>{MARKETING_SHELL_CSS}</style>
    <DotShaderBackground />
    <header className="m-header">
      <div className="m-logo">
        <Link to="/" className="m-logo-link">
          <span className="m-logo-mark" aria-hidden="true">
            <img src={LOGO_IMG_SRC} alt="" width={32} height={32} decoding="async" />
          </span>
          <span className="m-logo-text">SetPilot</span>
        </Link>
      </div>
      <div className="m-nav-right">
        <nav className="m-nav-links" aria-label="Site">
          <Link to="/about">About</Link>
          <Link to="/how-it-works">How it works</Link>
          <Link to="/blog">Blog</Link>
          <Link to="/contact">Contact</Link>
        </nav>
        <Link to="/" className="m-cta">
          Open app
        </Link>
      </div>
    </header>
    <main className="m-main">
      <Analytics />
      {children}
    </main>
    <footer className="m-footer">
      <div className="m-footer-inner">
        <span className="m-footer-brand">
          <span className="m-footer-mark" aria-hidden="true">
            <img src={LOGO_IMG_SRC} alt="" width={20} height={20} decoding="async" />
          </span>
          SetPilot
        </span>
        <span>An assistant for DJs who care about key, energy, and the story of a night.</span>
        <nav className="m-footer-links" aria-label="Legal and site">
          <Link to="/privacy-policy">Privacy policy</Link>
          <Link to="/terms-of-service">Terms of service</Link>
          <Link to="/contact">Contact</Link>
          <Link to="/about">About</Link>
        </nav>
      </div>
    </footer>
  </div>
);
